import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { Alert, Button, Card, Form, Input, Typography } from "antd";
import { checkGate, getStoredPassword, submitPassword } from "../lib/betaGate";
import type { BetaRoute } from "../lib/betaGate";

type GateState = "checking" | "blocked" | "needsPassword" | "unlocked";

interface BetaGateProps {
  route: BetaRoute;
  children: ReactNode;
}

/**
 * Wraps the public chatbot routes /agency and /vendor with a beta access gate
 * (OAIP-62). Calls the backend probe on mount; renders truly blank when the
 * caller's IP is not in the NC state allowlist, prompts for the per-route
 * password otherwise. Once unlocked, the password lives in sessionStorage and
 * is re-attached to subsequent chat requests via lib/api.ts.
 */
export default function BetaGate({ route, children }: BetaGateProps) {
  const [state, setState] = useState<GateState>("checking");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const result = await checkGate(route);
      if (cancelled) return;
      if (!result.ipAllowed) {
        setState("blocked");
        return;
      }
      const stored = getStoredPassword(route);
      setState(stored ? "unlocked" : "needsPassword");
    })();
    return () => {
      cancelled = true;
    };
  }, [route]);

  if (state === "checking" || state === "blocked") {
    // Truly blank — no DOM, no logo, no error. Out-of-network visitors should
    // not be able to tell whether the route exists.
    return null;
  }

  if (state === "unlocked") {
    return <>{children}</>;
  }

  const onFinish = async ({ password }: { password: string }) => {
    setError(null);
    setSubmitting(true);
    try {
      const ok = await submitPassword(route, password);
      if (ok) {
        setState("unlocked");
      } else {
        setError("Incorrect password.");
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not verify password.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <Card style={{ width: "100%", maxWidth: 420 }}>
        <Typography.Title level={4} style={{ marginTop: 0 }}>
          Beta access
        </Typography.Title>
        <Typography.Paragraph type="secondary">
          Enter the password provided by OAIP to continue.
        </Typography.Paragraph>
        <Form layout="vertical" onFinish={onFinish} disabled={submitting}>
          <Form.Item
            name="password"
            label="Password"
            rules={[{ required: true, message: "Password is required" }]}
          >
            <Input.Password autoFocus autoComplete="current-password" />
          </Form.Item>
          {error ? (
            <Alert
              type="error"
              message={error}
              showIcon
              style={{ marginBottom: 16 }}
            />
          ) : null}
          <Button type="primary" htmlType="submit" loading={submitting} block>
            Continue
          </Button>
        </Form>
      </Card>
    </div>
  );
}
